import { useEffect, useCallback, useRef } from "react";

// Internal building block for useCloseOnOutsideOrEscape (the actual public hook, see below) — not
// exported on its own since every consumer in this codebase wants click-outside and Escape wired to
// the same handler.
const useClickOutside = (
  ref,
  handler,
  {
    enabled = true,
    events = ["mousedown", "touchstart"],
    excludeSelectors = [],
  } = {},
) => {
  const handleClickOutside = useCallback(
    (event) => {
      if (!enabled) return;

      if (excludeSelectors.length > 0) {
        const isExcluded = excludeSelectors.some((selector) => {
          return event.target.closest(selector);
        });
        if (isExcluded) return;
      }

      const refs = Array.isArray(ref) ? ref : [ref];

      const isOutside = refs.every((r) => {
        return r.current && !r.current.contains(event.target);
      });

      if (isOutside) {
        handler(event);
      }
    },
    [ref, handler, enabled, excludeSelectors],
  );

  useEffect(() => {
    if (!enabled) return;

    events.forEach((eventName) => {
      document.addEventListener(eventName, handleClickOutside);
    });

    return () => {
      events.forEach((eventName) => {
        document.removeEventListener(eventName, handleClickOutside);
      });
    };
  }, [handleClickOutside, enabled, events]);
};

let escapeDepth = 0;

const useEscapeKey = (handler, enabled = true) => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;

    const myDepth = ++escapeDepth;

    const handleEscape = (event) => {
      if (event.key === "Escape" && escapeDepth === myDepth) {
        handlerRef.current(event);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      escapeDepth--;
    };
  }, [enabled]);
};

/**
 * Closes an element on an outside click or on Escape.
 *
 * @param {React.RefObject|React.RefObject[]} ref - One or more refs to the element(s)
 * @param {Function} handler - Called on an outside click or on Escape
 * @param {boolean} enabled - Whether the hook is active (default true)
 * @param {Object} options - Options
 * @param {string[]} options.events - Click event types to listen for (default ['mousedown', 'touchstart'])
 * @param {string[]} options.excludeSelectors - CSS selectors of elements to exclude
 *
 * @example
 *  Basic usage
 * useCloseOnOutsideOrEscape(dropdownRef, () => setIsOpen(false));
 *
 * @example
 *  Conditional
 *  Only active while the modal is open (enabled: isOpen)
 * useCloseOnOutsideOrEscape(dropdownRef, () => setIsOpen(false), isOpen);
 *
 * @example
 *  Multiple elements
 *  A click outside both elements closes the dropdown
 * useCloseOnOutsideOrEscape([dropdownRef, buttonRef], () => setIsOpen(false));
 *
 * @example
 *  With exclusions
 *  Don't close on clicks inside modals, tooltips, etc.
 * useCloseOnOutsideOrEscape(dropdownRef, () => setIsOpen(false), true, {
 *   excludeSelectors: ['.modal', '#tooltip']
 * });
 */
export const useCloseOnOutsideOrEscape = (ref, handler, enabled = true, options = {}) => {
  useClickOutside(ref, handler, { enabled, ...options });
  useEscapeKey(handler, enabled);
};
